import React from "react";
import { motion } from "framer-motion";
import { CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";

const HERO_IMAGE = "https://images.unsplash.com/photo-1735574698859-a3d3755c2b25?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxicml0aXNoJTIwYm9hcmRpbmclMjBzY2hvb2wlMjBhcmNoaXRlY3R1cmUlMjBoaXN0b3JpY3xlbnwwfHx8fDE3NzIxODQyNTl8MA&ixlib=rb-4.1.0&q=85";
const CAL_LINK = "https://cal.com/elite-school-advisory/30min";

const trustBullets = [
  "350+ partner schools worldwide",
  "Trusted by elite UK boarding schools",
  "Official test centre for partner schools",
];

const Hero = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const openCalendar = () => {
    window.open(CAL_LINK, "_blank");
  };

  return (
    <section
      id="hero"
      className="min-h-[90vh] md:min-h-screen flex items-center pt-20 md:pt-24 pb-10 md:pb-0 bg-[#FDFCF8] overflow-hidden"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="order-2 lg:order-1"
          >
            {/* Eyebrow */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-sm uppercase tracking-[0.2em] font-semibold text-[#C9A227] mb-6"
            >
              Elite Boarding School Advisory
            </motion.p>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-[#1A202C] leading-[1.15] tracking-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Your Child's Future,{" "}
              <span className="text-[#2B4C7E]">Strategically Placed</span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-lg md:text-xl text-[#4A5568] leading-relaxed mb-8 max-w-xl"
            >
              End-to-end strategic advisory for families seeking admission to the
              world's most prestigious boarding schools.
            </motion.p>

            {/* Trust Bullets */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex flex-col gap-3 mb-10"
            >
              {trustBullets.map((bullet, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C9A227] flex-shrink-0" />
                  <span className="text-[#4A5568] text-base">{bullet}</span>
                </div>
              ))}
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4"
            >
              <Button
                onClick={openCalendar}
                className="bg-[#2B4C7E] text-white hover:bg-[#1E3A60] rounded-[2px] px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group"
                data-testid="hero-primary-cta"
              >
                Book a Private Strategy Call
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => scrollToSection("#contact")}
                variant="outline"
                className="border-2 border-[#2B4C7E] text-[#2B4C7E] hover:bg-[#2B4C7E] hover:text-white bg-transparent rounded-[2px] px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base font-medium transition-all duration-300"
                data-testid="hero-secondary-cta"
              >
                Request the School Fit Checklist
              </Button>
            </motion.div>

            {/* For Whom */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="text-sm text-[#4A5568] mt-8 italic"
            >
              For discerning families seeking admission to UK, US, Swiss, and other
              leading boarding schools.
            </motion.p>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
            className="order-1 lg:order-2 relative"
          >
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-[#C9A227] rounded-tl-3xl" />
              <img
                src={HERO_IMAGE}
                alt="Historic British boarding school architecture"
                className="w-full h-[400px] lg:h-[550px] object-cover rounded-tl-[80px] rounded-br-[80px] shadow-2xl"
                loading="eager"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-[#2B4C7E] rounded-br-3xl" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
